import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { EventModel } from '@app/events/shared/event.model';
import { AlertCircle, AlertTriangle, CheckCircle, Edit, HelpCircle, Link2, Lock, LucideAngularModule, Plus, Trash } from 'lucide-angular';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Column } from '@app/partials/datatable/data-table/column';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, firstValueFrom, skip, takeUntil } from 'rxjs';
import { EventProductItemService } from '@app/events/shared/event-product-item.service';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { EventProductItemModel } from '@app/events/shared/event-product-item.model';
import { EventTimelineItemModel } from '@app/events/shared/event-timeline-item.model';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { ProductItemTagColumnComponent } from '@app/events/view/product-item-tag-column.component';
import { ProductItemPriceColumnComponent } from '@app/events/view/product-item-price-column.component';
import { ProductModelUtils } from '@app/products/shared/product.model';
import { format } from 'date-fns';
import { EventService, FinancialSummary } from '@app/events/shared/event.service';
import { CurrencyPipe, formatCurrency, NgClass } from '@angular/common';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { RightSidebarComponent } from '@app/partials/right-sidebar/right-sidebar.component';
import { ProductItemFormComponent } from '@app/events/view/product-item-form.component';
import { ProductItemEditFormComponent } from '@app/events/view/product-item-edit-form.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { PaymentModel } from '@app/events/shared/payment.model';
import { PaymentService } from '@app/events/shared/payment.service';
import { PaymentFormComponent } from '@app/events/view/payment-form.component';
import { PaymentEditFormComponent } from '@app/events/view/payment-edit-form.component';
import { RouterLink } from '@angular/router';
import { PaymentAmountWrapperComponent } from '@app/events/view/payment-amount-wrapper.component';
import { FeatureFlags } from '@app/config/feature-flags';
import { OfferProductsSelectionService } from '@app/events/shared/offer-products-selection.service';
import { OfferProductsSelectionComponent, OfferSelectionPayload } from '@app/events/view/offer-products-selection.component';
import { ToastService } from '@app/shared/toast/toast.service';
import { AuthService } from '@app/auth/auth.service';
import { UserModel } from '@app/users/shared/user.model';
import { DateUtil } from '@app/shared/utils/date.util';
import { buildEventProductItemCreatePayload } from '@app/events/shared/event-product-item-payload';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { EventAdjustmentService, EventAdjustmentPayload } from '@app/events/shared/event-adjustment.service';
import { EventAdjustmentModel } from '@app/events/shared/event-adjustment.model';
import { AdjustmentLineFormComponent } from '@app/events/view/adjustment-line-form.component';

@Component({
  selector: 'app-financial-summary',
  imports: [
    LucideAngularModule,
    DataTableComponent,
    TextInputComponent,
    SelectComponent,
    CurrencyPipe,
    ButtonComponent,
    RightSidebarComponent,
    ProductItemFormComponent,
    ProductItemEditFormComponent,
    ModalComponent,
    PaymentFormComponent,
    PaymentEditFormComponent,
    NgClass,
    RouterLink,
    OfferProductsSelectionComponent,
    AdjustmentLineFormComponent,
  ],
  templateUrl: './financial-summary.component.html',
  standalone: true,
})
export class FinancialSummaryComponent extends Destroyer implements OnInit {
  @Input() public event!: EventModel;
  @Output() timelineNavigate = new EventEmitter<string>();
  @Output() eventDataChanged = new EventEmitter<void>();

  protected productItemsPage?: PagedEntities<EventProductItemModel>;
  protected deleteProductItem?: EventProductItemModel;
  protected editProductItem?: EventProductItemModel;
  protected deletingProduct = false;

  // VG-408: Warning for qualifying product removal
  protected showQualifyingProductWarning = false;
  protected pendingDeleteProductItem?: EventProductItemModel;

  protected deletePayment?: PaymentModel;
  protected editPayment?: PaymentModel;

  protected paymentsPage?: PagedEntities<PaymentModel>;

  protected financialSummary: FinancialSummary | null = null;
  protected isAdmin = false;
  protected currentUser: UserModel | null = null;
  protected showDjEntertainmentWarning = false;
  protected readonly djEntertainmentWarningTitle = 'DJ & Entertainment Payment Update';

  protected readonly PlusIcon = Plus;
  protected readonly TrashIcon = Trash;
  protected readonly EditIcon = Edit;
  protected readonly LinkIcon = Link2;
  protected readonly ItineraryLinkIcon = CheckCircle;
  protected readonly HelpCircleIcon = HelpCircle;
  protected readonly LockIcon = Lock;
  protected readonly AlertCircleIcon = AlertCircle;
  protected readonly AlertTriangleIcon = AlertTriangle;

  protected isRightSidebarOpen = false;
  protected rightSideBarContent?: string;
  protected timelineItems: EventTimelineItemModel[] = [];
  protected timelineItemChoices: { value: number | null; label: string }[] = [
    { value: null, label: 'No itinerary item' },
  ];
  protected unlinkedOnlyControl = new FormControl(false, { nonNullable: true });
  protected linkProductItem?: EventProductItemModel;
  protected linkTimelineItemControl = new FormControl<number | null>(null);
  protected linkingTimeline = false;

  tableColumns: Column<EventProductItemModel>[] = [
    {
      field: 'name',
      header: 'Name',
      width: '45%',
      component: ProductItemTagColumnComponent,
    },
    {
      field: 'category',
      header: 'Category',
      width: '20%',
      align: 'center',
      getColor: (item: EventProductItemModel) => {
        return item.product?.category
          ? ProductModelUtils.getCategoryColor(item.product.category)
          : '';
      },
      callback: (item: EventProductItemModel) => {
        if (item.product?.category) {
          return item.product.category;
        }
        return this.isVenueHireProduct(item) ? 'Venue Hire' : '';
      },
    },
    {
      field: 'itinerary' as keyof EventProductItemModel,
      header: 'Found in itinerary',
      width: '20%',
      align: 'center',
      // TVG-82: Show only the time and a tick that links through to the
      // itinerary. The date is intentionally omitted; the time stays because
      // products will eventually sit chronologically within the itinerary.
      callback: (item: EventProductItemModel) => {
        const timelineItem = item.event_timeline_item;
        if (!timelineItem?.datetime) {
          return '';
        }
        return format(new Date(timelineItem.datetime), 'HH:mm');
      },
      icon: (item: EventProductItemModel) => {
        return item.event_timeline_item ? this.ItineraryLinkIcon : null;
      },
      clickHandler: (item: EventProductItemModel) => {
        const timelineItem = item.event_timeline_item;
        if (timelineItem) {
          this.timelineNavigate.emit(timelineItem.id + '');
        }
      },
    },
    {
      field: 'qty',
      header: 'Quantity',
      width: '15%',
      align: 'center',
      callback: (item: EventProductItemModel) => {
        const variantTotal = item.variant_selections?.reduce((sum, vs) => sum + (vs.quantity || 0), 0) ?? 0;
        const baseQty = item.quantity ?? 0;
        const isVariantBased = item.product?.is_variant_based ?? false;

        // For menus and other non-variant-based products, the overall quantity should come from the base item.
        // Variant quantities represent per-variant allocations (e.g., starters, mains) and should not be summed.
        if (!isVariantBased) {
          return baseQty ? baseQty.toString() : variantTotal ? variantTotal.toString() : '';
        }

        // Variant-based products rely on variant quantities exclusively.
        return variantTotal ? variantTotal.toString() : '';
      },
    },
    {
      field: 'price',
      header: 'Price',
      width: '20%',
      align: 'center',
      component: ProductItemPriceColumnComponent,
      getColor: (item: EventProductItemModel) => {
        if (this.isVenueHireProduct(item)) {
          return '';
        }
        return item.is_discontinued ? 'orange' : '';
      },
    },
  ];

  get rowActions() {
    return [
      {
        icon: this.LinkIcon,
        callback: (item: EventProductItemModel) => {
          this.openTimelineLinkModal(item);
        },
        routerLink: () => {
          return [];
        },
        classes: 'text-gray-500 hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400',
        title: () => 'Link to itinerary item',
      },
      {
        icon: this.EditIcon,
        callback: (item: EventProductItemModel) => {
          this.editProductItem = item;
          this.openRightSidebar('product-item-edit');
        },
        routerLink: (item: EventProductItemModel) => {
          return [];
        },
        classes: 'text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500',
        disabled: () => false,
        title: () => 'Edit product',
      },
      {
        icon: this.TrashIcon,
        callback: (item: EventProductItemModel) => {
          // Check if deletion is allowed
          if (!this.canDeleteProduct(item)) {
            // Don't show alert here as tooltip provides the info
            return;
          }
          this.deleteProductItem = item;
        },
        routerLink: (item: EventProductItemModel) => {
          return [];
        },
        classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-gray-500',
        disabled: (item: EventProductItemModel) => !this.canDeleteProduct(item),
        title: (item: EventProductItemModel) => {
          if (this.isEventConfirmed) {
            return 'Cannot delete products from confirmed events';
          }
          if (this.isVenueHireProduct(item) && this.event.category === 'Wedding') {
            return 'Venue Hire is required for wedding events';
          }
          return 'Delete product';
        },
      },
    ];
  }

  paymentsTableColumns: Column<PaymentModel>[] = [
    {
      field: 'description',
      header: 'Description',
      width: '25%',
    },
    {
      field: 'amount',
      header: 'Amount',
      width: '15%',
      component: PaymentAmountWrapperComponent,
    },
    {
      field: 'date_due',
      header: 'Due Date',
      width: '15%',
      callback: (item: PaymentModel) => {
        return item.date_due ? format(new Date(item.date_due), 'MMM d, yyyy') : '';
      },
    },
    {
      field: 'date_paid',
      header: 'Paid Date',
      width: '15%',
      callback: (item: PaymentModel) => {
        return item.date_paid ? format(new Date(item.date_paid), 'MMM d, yyyy') : '';
      },
    },
    {
      field: 'amount_paid',
      header: 'Amount Paid',
      width: '15%',
      callback: (item: PaymentModel) => {
        return item.amount_paid ? formatCurrency(item.amount_paid, 'en-GB', '£') : '';
      },
    },
  ];

  get paymentsTableRowActions() {
    if (!this.isAdmin) {
      return [];
    }

    return [
      {
        icon: this.EditIcon,
        type: 'link',
        callback: (item: PaymentModel) => {
          this.editPayment = item;
          this.openRightSidebar('payment-edit');
        },
        routerLink: (item: PaymentModel) => {
          return [];
        },
        classes: 'text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500',
      },
      {
        icon: this.TrashIcon,
        type: 'link',
        callback: (item: PaymentModel) => {
          this.deletePayment = item;
        },
        routerLink: (item: PaymentModel) => {
          return [];
        },
        classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
        condition: (item: PaymentModel) => !item.is_system_generated,
      },
    ];
  }

  confirmDelete() {
    if (this.deletingProduct) return;

    if (this.deleteProductItem) {
      // Safety check - should not reach here if button is properly disabled
      if (!this.canDeleteProduct(this.deleteProductItem)) {
        this.deleteProductItem = undefined;
        return;
      }

      // VG-408: Check if removing a qualifying product would invalidate the offer
      if (this.shouldWarnAboutQualifyingProductRemoval(this.deleteProductItem)) {
        this.pendingDeleteProductItem = this.deleteProductItem;
        this.deleteProductItem = undefined;
        this.showQualifyingProductWarning = true;
        return;
      }

      this.proceedWithDelete(this.deleteProductItem);
    }
  }

  // VG-408: Check if warning is needed for qualifying product removal
  private shouldWarnAboutQualifyingProductRemoval(productItem: EventProductItemModel): boolean {
    // Only warn if:
    // 1. The product counts toward the £500 deal
    // 2. There are currently applied offer products
    // 3. Removing this product would drop below £500 threshold

    if (!productItem.product.counts_to_500_deal) {
      return false;
    }

    if (this.offerDiscountApplied <= 0) {
      return false;
    }

    // Calculate what the qualifying total would be after removing this product
    const productValue = productItem.calculated_price ?? 0;
    const newQualifyingTotal = this.qualifyingTotal - productValue;

    return newQualifyingTotal < 500;
  }

  // VG-408: Confirm qualifying product deletion
  confirmQualifyingProductDelete() {
    if (this.pendingDeleteProductItem) {
      this.proceedWithDelete(this.pendingDeleteProductItem);
      this.pendingDeleteProductItem = undefined;
      this.showQualifyingProductWarning = false;
    }
  }

  // VG-408: Cancel qualifying product deletion
  cancelQualifyingProductDelete() {
    this.pendingDeleteProductItem = undefined;
    this.showQualifyingProductWarning = false;
  }

  // VG-408: Extract actual deletion logic to separate method
  private proceedWithDelete(productItem: EventProductItemModel) {
    this.deletingProduct = true;

    this.eventService.deleteEventProductItem(productItem.id).subscribe({
      next: () => {
        this.deleteProductItem = undefined;
        this.deletingProduct = false;
        this.loadData();
        // VG-416: Refresh financial summary to reflect deleted product item
        this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
          this.updateFinancialSummary(res);
        });
        // VG-413: Emit event data changed to refresh itinerary
        this.eventDataChanged.emit();
      },
      error: (res) => {
        console.error('Error deleting product:', res);
        if (res.status === 403) {
          this.confirmationModal.alert({
            title: 'Cannot delete product',
            message: res.error?.message || 'Cannot delete this product',
            confirmLabel: 'OK',
          });
        }
        this.deleteProductItem = undefined;
        this.deletingProduct = false;
      },
    });
  }

  searchControl = new FormControl();
  routeParams!: RouteParams;

  protected categoriesControl = new FormControl();
  protected paymentSyncControl = new FormControl();
  private skipNextRouteEvent = false;
  private lastRouteKey: string | null = null;

  // VG-408: Offer products selection
  protected isApplyingOffer = false;
  protected offerSelectionError: string | null = null;
  protected sidebarCustomWidth?: number;
  protected productEditResetToken = 0;
  @ViewChild('productItemForm') productItemFormComp?: any;
  @ViewChild('productItemEditForm') productItemEditFormComp?: any;
  @ViewChild('adjustmentForm') adjustmentFormComp?: AdjustmentLineFormComponent;

  // TVG-156: admin-only discount / adjustment lines on the event finance.
  protected adjustments: EventAdjustmentModel[] = [];
  protected savingAdjustment = false;

  protected categories = [
    { value: 'Venue Hire', label: 'Venue Hire' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Wedding Morning Ideas – Food', label: 'Wedding Morning Ideas – Food' },
    { value: 'Wedding Morning Ideas – Drink', label: 'Wedding Morning Ideas – Drink' },
    { value: 'Canapes', label: 'Canapes' },
    { value: 'Reception Drinks', label: 'Reception Drinks' },
    { value: 'Wedding Breakfast Menus', label: 'Wedding Breakfast Menus' },
    { value: 'Drinks Packages', label: 'Drinks Packages' },
    { value: 'Wine List', label: 'Wine List' },
    { value: 'Evening Food', label: 'Evening Food' },
    { value: 'Complimentary Items', label: 'Complimentary Items' },
    { value: 'Extras – Food', label: 'Extras – Food' },
    { value: 'Extras – Drink', label: 'Extras – Drink' },
    { value: 'DJ & Entertainment', label: 'DJ & Entertainment' },
    { value: 'Hire Items', label: 'Hire Items' },
    { value: 'Décor', label: 'Décor' },
    { value: 'Floristry', label: 'Floristry' },
    { value: 'Miscellaneous', label: 'Miscellaneous' },
    { value: 'Supplier Meals', label: 'Supplier Meals' },
    { value: 'Security Retainer', label: 'Security Retainer' },
  ];

  constructor(
    private service: EventProductItemService,
    private eventService: EventService,
    private paymentService: PaymentService,
    private offerProductsService: OfferProductsSelectionService,
    private toastService: ToastService,
    private authService: AuthService,
    private confirmationModal: ConfirmationModalService,
    private featureToggles: FeatureToggleService,
    private adjustmentService: EventAdjustmentService,
  ) {
    super();
  }

  // TVG-128 / TVG-206: gates the aggregated Payment Summary box on the staff
  // event screen. Independent of the customer/PDF toggle so staff can see the
  // box while it stays hidden from customers and the PDF.
  get paymentSummaryBoxEnabled(): boolean {
    return this.featureToggles.isEnabled('payment-summary-box-staff');
  }

  loadData() {
    this.service.getPage(this.routeParams).subscribe((res) => {
      this.applyProductItemsPage(res);
      // Load payments after products are loaded so enrichment can use product data
      this.loadPayments();
    });

    this.loadTimelineItems();
    this.loadAdjustments();

    this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
      this.updateFinancialSummary(res);
    });
  }

  // TVG-156: load the admin-only discount / adjustment lines for this event.
  private loadAdjustments(): void {
    this.adjustmentService.list(this.event.id).subscribe({
      next: (res) => {
        this.adjustments = res.data ?? [];
      },
      error: () => {
        this.adjustments = [];
      },
    });
  }

  // TVG-156: persist a new adjustment line, then refresh the lines and totals.
  adjustmentCreated(payload: EventAdjustmentPayload): void {
    if (this.savingAdjustment) {
      return;
    }
    this.savingAdjustment = true;

    this.adjustmentService.create(this.event.id, payload).subscribe({
      next: () => {
        this.savingAdjustment = false;
        this.adjustmentFormComp?.reset();
        this.closeRightSidebar();
        this.loadAdjustments();
        this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
          this.updateFinancialSummary(res);
        });
        this.eventDataChanged.emit();
      },
      error: (res) => {
        this.savingAdjustment = false;
        const message = res?.error?.message || 'Failed to add adjustment line.';
        this.adjustmentFormComp?.applyServerError(message);
        this.toastService.error(message);
      },
    });
  }

  // TVG-156: remove an adjustment line (admin only), then refresh totals.
  deleteAdjustmentLine(adjustment: EventAdjustmentModel): void {
    if (!this.isAdmin || !adjustment.id) {
      return;
    }

    this.confirmationModal
      .confirm({
        title: 'Remove adjustment line',
        message: `Remove "${adjustment.description}" from this event?`,
        confirmLabel: 'Remove',
        cancelLabel: 'Cancel',
      })
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.adjustmentService.delete(adjustment.id!).subscribe({
          next: () => {
            this.loadAdjustments();
            this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
              this.updateFinancialSummary(res);
            });
            this.eventDataChanged.emit();
          },
          error: (res) => {
            const message = res?.error?.message || 'Failed to remove adjustment line.';
            this.toastService.error(message);
          },
        });
      });
  }

  private loadTimelineItems(): void {
    this.eventService.getTimelineItems(this.event.id).subscribe({
      next: (res) => {
        this.timelineItems = (res.data ?? []).slice().sort((a, b) => {
          return new Date(a.datetime).getTime() - new Date(b.datetime).getTime();
        });
        this.event.timeline_items = this.timelineItems;
        this.timelineItemChoices = [
          { value: null, label: 'No itinerary item' },
          ...this.timelineItems.map((item) => ({
            value: item.id,
            label: `${format(new Date(item.datetime), 'HH:mm MMM d yyyy')} - ${item.name}`,
          })),
        ];
      },
      error: () => {
        this.timelineItems = [];
        this.timelineItemChoices = [{ value: null, label: 'No itinerary item' }];
      },
    });
  }

  private applyProductItemsPage(page: PagedEntities<EventProductItemModel>) {
    // TVG-81: the backend returns products in the staff drag-and-drop order
    // (sort_order), the single source of truth across every surface. No client
    // re-sort here — preserve the order exactly as delivered.
    this.productItemsPage = page;
  }

  /**
   * TVG-81: drag reorder is only offered when the table shows the full, unfiltered
   * product list on a single page — reordering a filtered or paginated subset
   * would be ambiguous.
   */
  get canReorderProducts(): boolean {
    // TVG-81: reordering is a cosmetic, price-neutral action, so it is allowed in
    // every staff-editable state (including Confirmed).
    const hasSearch = String(this.searchControl.value ?? '').trim() !== '';
    const hasCategoryFilter = ((this.categoriesControl.value ?? []) as string[]).length > 0;
    const unlinkedOnly = !!this.unlinkedOnlyControl.value;
    const singlePage = (this.productItemsPage?.meta?.last_page ?? 1) <= 1;

    return !hasSearch && !hasCategoryFilter && !unlinkedOnly && singlePage;
  }

  /**
   * TVG-81: persist the new drag order. The list is updated optimistically by the
   * data-table; on failure we reload to resync with the server.
   */
  onProductsReordered(orderedIds: (number | string)[]): void {
    const ids = orderedIds.map((id) => Number(id));

    this.service.reorder(this.event.id, ids).subscribe({
      next: () => {
        this.toastService.success('Product order updated');
        // Other surfaces (PDF, customer portal) read the persisted order.
        this.eventDataChanged.emit();
      },
      error: () => {
        this.toastService.error('Could not update product order. Reloading…');
        this.loadData();
      },
    });
  }

  private loadPayments() {
    this.paymentService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 1000,
          filter: [{ field: 'event_id', value: this.event.id }],
          // Don't sort - backend sorts by date_due NULLS FIRST by default
        }),
      )
      .subscribe((res) => {
        this.applyPaymentsPage(res);
      });
  }

  private applyPaymentsPage(page: PagedEntities<PaymentModel>) {
    // Enrich payment data with venue hire breakdown info
    if (page.data) {
      page.data = page.data.map(payment => this.enrichPaymentData(payment));
    }
    this.paymentsPage = page;
  }

  private enrichPaymentData(payment: PaymentModel): PaymentModel {
    // Add venue hire breakdown information if applicable
    if (payment.description === 'First Payment' && this.event) {
      const reservationFeeAmount = parseFloat(this.event.reservation_fee?.toString() || '0') || 0;

      // Calculate actual venue hire items total from product items
      const venueHireTotal = this.calculateVenueHireTotal();

      // Remove any commas or formatting from the amount string before parsing
      const cleanAmount = payment.amount?.toString().replace(/,/g, '') || '0';
      const paymentAmount = parseFloat(cleanAmount) || 0;

      // Show breakdown if we have venue hire items
      if (venueHireTotal > 0 && reservationFeeAmount > 0) {
        if (venueHireTotal < reservationFeeAmount) {
          // Reservation fee exceeds venue hire - there's a credit
          const credit = reservationFeeAmount - venueHireTotal;
          (payment as any).venueHireBreakdown = `£${this.formatNumber(venueHireTotal)} - £${this.formatNumber(reservationFeeAmount)} reservation fee = £${this.formatNumber(credit)} credit`;
        } else {
          // Normal case - reservation fee partially covers venue hire
          (payment as any).venueHireBreakdown = `£${this.formatNumber(venueHireTotal)} - £${this.formatNumber(reservationFeeAmount)} reservation fee`;
        }
      } else if (venueHireTotal > 0) {
        // Show total venue hire without reservation fee info
        (payment as any).venueHireBreakdown = `Total venue hire items: £${this.formatNumber(venueHireTotal)}`;
      }
    }

    return payment;
  }

  private calculateVenueHireTotal(): number {
    if (!this.productItemsPage || !this.productItemsPage.data) {
      return 0;
    }

    return this.productItemsPage.data
      .filter(item => {
        const productName = (item.product.name || '').toLowerCase();
        const productCategory = item.product.category || '';
        return productName === 'venue hire' || productCategory === 'Accommodation';
      })
      .reduce((total, item) => {
        // Prioritize manual amount, then calculated_price
        let itemTotal = 0;
        if (item.amount !== undefined && item.amount !== null) {
          itemTotal = parseFloat(item.amount.toString());
        } else if (item.calculated_price !== undefined && item.calculated_price !== null) {
          itemTotal = parseFloat(item.calculated_price.toString());
        }
        return total + itemTotal;
      }, 0);
  }

  private getVenueHirePriceFromEvent(): number {
    // Try to get hire price from venue daily pricing if available
    const venue = this.event?.venue as any;
    if (venue?.daily_prices && this.event.start_date) {
      const startDate = new Date(this.event.start_date).toISOString().split('T')[0];
      const dailyPrice = venue.daily_prices.find((price: any) =>
        price.date === startDate
      );
      return dailyPrice?.hire_price || 0;
    }
    return 0;
  }

  private formatNumber(amount: number | string): string {
    const numAmount = typeof amount === 'number' ? amount : parseFloat(amount?.toString() || '0');
    return numAmount.toFixed(2);
  }

  ngOnInit(): void {
    this.loadCurrentUser();

    this.routeParams = new RouteParams({
      pageNum: 1,
      pageSize: 100,
      filter: [{ field: 'event_id', value: this.event.id }],
    });

    // Initialize payment sync checkbox with current event value
    this.paymentSyncControl.setValue(this.event.payment_sync_enabled);

    this.lastRouteKey = this.buildRouteKey(this.routeParams);
    this.skipNextRouteEvent = true;
    this.loadData();

    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyProductFilters());

    this.categoriesControl.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev ?? []) === JSON.stringify(curr ?? [])),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.applyProductFilters());

    this.unlinkedOnlyControl.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyProductFilters());

    // Handle payment sync checkbox changes
    this.paymentSyncControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((enabled: boolean) => {
      this.updatePaymentSyncSetting(enabled);
    });
  }

  private applyProductFilters(): void {
    const filters: Array<{ field: string; value: string | number }> = [
      { field: 'event_id', value: this.event.id },
    ];

    const search = String(this.searchControl.value ?? '').trim();
    if (search) {
      filters.push({ field: 'search', value: search });
    }

    const categories = (this.categoriesControl.value ?? []) as string[];
    if (categories.length > 0) {
      filters.push({ field: 'product.category', value: categories.join(',') });
    }

    if (this.unlinkedOnlyControl.value) {
      filters.push({ field: 'unlinked', value: '1' });
    }

    this.routeParams.filter = filters;
    this.loadData();
  }

  private loadCurrentUser(): void {
    this.authService
      .getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.currentUser = user;
          this.isAdmin = user?.roles?.includes('admin') ?? false;
        },
        error: () => {
          this.isAdmin = false;
          this.currentUser = null;
        },
      });
  }

  handleRoute(params: RouteParams) {
    const nextRouteKey = this.buildRouteKey(params);
    const isSameRoute = this.lastRouteKey === nextRouteKey;

    if (this.skipNextRouteEvent && isSameRoute) {
      this.skipNextRouteEvent = false;
      return;
    }

    if (isSameRoute) {
      return;
    }

    this.skipNextRouteEvent = false;
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.lastRouteKey = nextRouteKey;
    this.loadData();
  }

  private buildRouteKey(params: RouteParams): string {
    const sortKey = (params.sort ?? [])
      .map((entry) => ({
        field: entry.sourceField ?? entry.field,
        dir: entry.dir,
      }))
      .sort((a, b) => `${a.field}${a.dir}`.localeCompare(`${b.field}${b.dir}`));

    return JSON.stringify({
      pageNum: params.pageNum ?? 0,
      pageSize: params.pageSize ?? 0,
      sort: sortKey,
    });
  }

  openTimelineLinkModal(item: EventProductItemModel): void {
    this.linkProductItem = item;
    if (this.timelineItems.length === 0) {
      this.linkTimelineItemControl.setValue(null);
      return;
    }

    this.linkTimelineItemControl.setValue(item.event_timeline_item_id ?? item.event_timeline_item?.id ?? null);
  }

  closeTimelineLinkModal(): void {
    if (this.linkingTimeline) {
      return;
    }
    this.linkProductItem = undefined;
    this.linkTimelineItemControl.setValue(null);
  }

  confirmTimelineLink(): void {
    if (!this.linkProductItem || this.linkingTimeline) {
      return;
    }

    this.linkingTimeline = true;

    this.service
      .updateTimelineLink(this.linkProductItem.id, this.linkTimelineItemControl.value ?? null)
      .subscribe({
        next: () => {
          this.linkingTimeline = false;
          this.closeTimelineLinkModal();
          this.loadData();
          this.eventDataChanged.emit();
          this.toastService.success('Product itinerary link updated');
        },
        error: (res) => {
          this.linkingTimeline = false;
          const message = res?.error?.message ?? 'Failed to update itinerary link';
          this.toastService.error(message);
        },
      });
  }

  openRightSidebar(content: string, customWidth?: number) {
    // For confirmed events, we still allow adding products but they get locked at current price
    this.sidebarCustomWidth = customWidth;
    this.isRightSidebarOpen = true;
    this.rightSideBarContent = content;
  }

  closeRightSidebar() {
    const closingContent = this.rightSideBarContent;
    this.isRightSidebarOpen = false;
    this.rightSideBarContent = undefined;
    this.sidebarCustomWidth = undefined;

    if (closingContent === 'offer-selection') {
      this.offerSelectionError = null;
      this.isApplyingOffer = false;
    }
  }

  productItemCreated(data: EventProductItemModel) {
    const payload = buildEventProductItemCreatePayload(data, this.event.id);

    this.eventService.createEventProductItem(payload).subscribe({
      next: (response) => {
        this.maybeShowDjEntertainmentPaymentWarning(response?.data);
        this.loadData();
        this.closeRightSidebar();
        // VG-416: Refresh financial summary to reflect new product item
        this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
          this.updateFinancialSummary(res);
        });
        // VG-413: Emit event data changed to refresh itinerary
        this.eventDataChanged.emit();
        console.log('Product item created, payment sync enabled:', this.event.payment_sync_enabled);
      },
      error: (res) => {
        const message = res?.error?.message || 'Failed to create product item.';
        if (this.productItemFormComp?.applyServerError) {
          this.productItemFormComp.applyServerError(message);
        }
        this.toastService.error(message);
        console.error('Error creating product item:', res);
      },
    });
  }

  productItemUpdated(data: { data: any; id: number }) {
    // Ensure we're passing variant information to the API
    const updatedData = {
      ...data.data,
      event_id: this.event.id,
    };

    this.eventService.updateEventProductItem(updatedData, data.id).subscribe({
      next: (response) => {
        this.loadData();
        this.closeRightSidebar();
        this.editProductItem = undefined;
        // VG-416: Refresh financial summary to reflect updated product item
        this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
          this.updateFinancialSummary(res);
        });
        // VG-413: Emit event data changed to refresh itinerary
        this.eventDataChanged.emit();
        console.log('Product item updated, payment sync enabled:', this.event.payment_sync_enabled);
      },
      error: (res) => {
        const message = res?.error?.message || 'Failed to update product item.';
        this.toastService.error(message);
        this.productEditResetToken++;
        if (this.productItemEditFormComp?.applyServerError) {
          this.productItemEditFormComp.applyServerError(message);
        }
        console.error('Error updating product item:', res);
      },
    });
  }

  paymentItemCreated(payment: PaymentModel) {
    if (!this.isAdmin) return;
    this.loadPayments();
    // Refresh financial summary to reflect new payment data
    this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
      this.updateFinancialSummary(res);
    });
    this.closeRightSidebar();
  }

  paymentItemUpdated(payment: PaymentModel) {
    if (!this.isAdmin) return;
    this.loadPayments();
    // Refresh financial summary to reflect updated payment data
    this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
      this.updateFinancialSummary(res);
    });
    this.closeRightSidebar();
    this.editPayment = undefined;
  }

  confirmDeletePayment() {
    if (!this.isAdmin) {
      return;
    }

    if (this.deletePayment) {
      this.paymentService.delete(this.deletePayment.id.toString()).subscribe({
        next: () => {
          this.deletePayment = undefined;
          this.loadPayments();
          // Refresh financial summary to reflect deleted payment
          this.eventService.getFinancialSummary(this.event.id).subscribe((res) => {
            this.updateFinancialSummary(res);
          });
        },
        error: (res) => {
          console.error('Error deleting payment:', res);
        },
      });
    }
  }

  private updatePaymentSyncSetting(enabled: boolean) {
    this.eventService.updatePaymentSync(this.event.id, enabled).subscribe({
      next: () => {
        this.event.payment_sync_enabled = enabled;
      },
      error: (res: any) => {
        console.error('Error updating payment sync setting:', res);
        // Revert the checkbox if the update failed
        this.paymentSyncControl.setValue(!enabled, { emitEvent: false });
      },
    });
  }

  // VG-342: Check both feature flag and event status
  get isEventConfirmed(): boolean {
    return FeatureFlags.PRICE_LOCKING && this.event?.status === 'Confirmed';
  }

  get isConfirmedStatus(): boolean {
    return this.event?.status === 'Confirmed';
  }

  get minimumSpendDisplay(): number {
    const baseValue = this.financialSummary?.minimum_spend ?? 0;
    const currentQualifiedTotal = this.financialSummary?.minimum_spend_total ?? 0;

    if (currentQualifiedTotal <= 0) {
      return baseValue;
    }

    // VG-432: Keep the displayed value anchored at the base until qualifying products meet/exceed it
    return Math.max(baseValue, currentQualifiedTotal);
  }

  get contractualMinimumSpend(): number {
    // Locked at confirmation: use contractual snapshot if present
    const contractualValue = this.financialSummary?.minimum_spend_contractual;
    if (contractualValue !== undefined && contractualValue !== null) {
      return contractualValue;
    }

    return this.minimumSpendDisplay;
  }

  get contractualMinimumSpendLeft(): number {
    const contractualLeft = this.financialSummary?.minimum_spend_contractual_left;
    if (contractualLeft !== undefined && contractualLeft !== null) {
      return Math.max(0, contractualLeft);
    }

    // Fallback to base minimum spend delta when contractual value is not set
    return Math.max(0, this.financialSummary?.minimum_spend_left ?? 0);
  }

  get isContractualMinimumMissing(): boolean {
    return this.isConfirmedStatus
      && (this.financialSummary?.minimum_spend_contractual === null
        || this.financialSummary?.minimum_spend_contractual === undefined);
  }

  // Check if product is Venue Hire
  isVenueHireProduct(productItem: EventProductItemModel): boolean {
    return productItem.product.name.toLowerCase() === 'venue hire';
  }

  // Check if product can be deleted
  canDeleteProduct(productItem: EventProductItemModel): boolean {
    return !(this.isVenueHireProduct(productItem) && this.event.category === 'Wedding');
  }

  private isEventLessThanNineMonthsAway(): boolean {
    const eventDate = DateUtil.parseDate(this.event?.start_date);
    if (!eventDate) {
      return false;
    }

    const threshold = new Date();
    threshold.setHours(0, 0, 0, 0);
    threshold.setMonth(threshold.getMonth() + 9);

    const eventDay = new Date(eventDate);
    eventDay.setHours(0, 0, 0, 0);

    return eventDay < threshold;
  }

  private isDjEntertainmentProduct(item: EventProductItemModel | null | undefined): boolean {
    const category = String(item?.product?.category ?? '');

    return category === 'DJ & Entertainment' || category === 'DJ/Entertainment';
  }

  private maybeShowDjEntertainmentPaymentWarning(item: EventProductItemModel | null | undefined): void {
    if (this.showDjEntertainmentWarning) {
      return;
    }

    if (!item || !this.isDjEntertainmentProduct(item) || !this.isEventLessThanNineMonthsAway()) {
      return;
    }

    this.showDjEntertainmentWarning = true;
  }

  // VG-408: £500 Offer getters and methods
  get qualifyingTotal(): number {
    return this.financialSummary?.qualifying_total ?? 0;
  }

  get isOfferEligible(): boolean {
    return this.financialSummary?.offer_eligible ?? false;
  }

  get offerDiscountApplied(): number {
    return this.financialSummary?.offer_discount_applied ?? 0;
  }

  get offerSelectionItems(): EventProductItemModel[] {
    return this.financialSummary?.offer_item_candidates ?? [];
  }

  get currentOfferItemIds(): number[] {
    if (this.financialSummary?.current_offer_item_ids?.length) {
      return this.financialSummary.current_offer_item_ids;
    }

    return this.productItemsPage?.data
      ?.filter((item) => item.is_offer_item)
      ?.map((item) => item.id)
      ?.filter((id): id is number => id !== null && id !== undefined) ?? [];
  }

  get blockedOfferItemIds(): number[] {
    return this.financialSummary?.blocked_offer_item_ids ?? [];
  }

  get offerItems(): EventProductItemModel[] {
    return this.productItemsPage?.data?.filter((item) => item['is_offer_item']) ?? [];
  }

  // VG-408: Get total value of selected offer products (before discount)
  get offerProductsTotal(): number {
    return this.offerItems.reduce((sum, item) => sum + (item.calculated_price ?? 0), 0);
  }

  get regularProducts(): EventProductItemModel[] {
    return this.productItemsPage?.data?.filter((item) => !item['is_offer_item']) ?? [];
  }

  get progressToOffer(): string {
    const total = Math.min(this.qualifyingTotal, 500);
    const percent = (total / 500) * 100;
    return `${percent}%`;
  }

  showOfferSelection(): void {
    this.offerSelectionError = null;
    this.isApplyingOffer = false;
    this.openRightSidebar('offer-selection', 45);
  }

  async applyOfferSelection(payload: OfferSelectionPayload): Promise<void> {
    if (this.isApplyingOffer) {
      return;
    }

    this.isApplyingOffer = true;
    this.offerSelectionError = null;

    const createdIds: number[] = [];
    try {
      // Create any new offer items first (but don't mark as offer yet)
      for (const item of payload.newItems ?? []) {
        const datePurchased = item.date_purchased ?? new Date().toISOString().slice(0, 19).replace('T', ' ');
        const createPayload: any = {
          quantity: item.quantity ?? null,
          amount: item.amount ?? null,
          date_purchased: datePurchased,
          is_paid: item.is_paid ?? false,
          product_id: item.product_id,
          event_id: this.event.id,
          variant_selections: item.variant_selections?.map(selection => ({
            variant_id: selection.variant_id,
            variant_option_id: selection.variant_option_id,
            quantity: selection.quantity,
            notes: selection.notes ?? null,
          })) ?? [],
        };

        const response = await firstValueFrom(this.eventService.createEventProductItem({
          ...createPayload,
        }));
        this.maybeShowDjEntertainmentPaymentWarning(response?.data);
        const newId = response?.data?.id;
        if (newId) {
          createdIds.push(newId);
        }
      }

      const finalOfferIds = [
        ...(payload.selectedExistingIds ?? []),
        ...createdIds,
      ];

      // Apply offer flags
      await firstValueFrom(this.offerProductsService.applyOfferProducts(this.event.id, finalOfferIds));

      // Remove previously applied offer items that were unchecked (delete from event)
      const idsToDelete = this.currentOfferItemIds.filter(id => !(payload.selectedExistingIds ?? []).includes(id));
      for (const deleteId of idsToDelete) {
        await firstValueFrom(this.eventService.deleteEventProductItem(deleteId));
      }

      // Refresh data
      this.updateFinancialSummary(await firstValueFrom(this.eventService.getFinancialSummary(this.event.id)));
      this.loadData();
      this.closeRightSidebar();
      this.toastService.success('Offer products updated');
    } catch (res: any) {
      console.error('Error applying offer products:', res);
      const message = res?.error?.message ?? 'Failed to apply offer products.';
      this.offerSelectionError = message;
      this.toastService.error(message);

      // Cleanup any newly created items if we failed mid-flight
      for (const createdId of createdIds) {
        try {
          await firstValueFrom(this.eventService.deleteEventProductItem(createdId));
        } catch (cleanupErr) {
          console.error('Failed to clean up created offer item', cleanupErr);
        }
      }
    } finally {
      this.isApplyingOffer = false;
    }
  }

  private updateFinancialSummary(summary: FinancialSummary | null): void {
    this.financialSummary = summary;
  }

}
